// src/services/address/gps-external/positionstack/adapter.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { API } from 'constants/Api';
import fetchX from 'utils/fetchX';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
// Note: Positionstack requires API key for production use
// Free tier: 25,000 requests/month, no HTTPS on free tier
const POSITIONSTACK_API_KEY = import.meta.env.VITE_POSITIONSTACK_API_KEY || '';

// *******************************************************************************
// ADAPTER INTERFACE
// *******************************************************************************

/**
 * Positionstack GPS Provider Adapter
 * Free tier available (25k requests/month), requires API key
 *
 * Implements provider contract:
 * - searchCoordinates(payload) → Promise<Object>
 * - normalizeRequestPayload(payload) → Object
 * - normalizeResponseBody(response) → Universal POJO
 */

/**
 * Searches GPS coordinates for given address
 * @param {Object} payload - Search parameters
 * @param {string} payload.address - Street address
 * @param {string} payload.number - Street number
 * @param {string} payload.city - City name
 * @param {string} payload.region - State/region code (e.g., 'SP', 'RJ')
 * @returns {Promise<Object>} Universal POJO with coordinates
 */
async function searchCoordinates(payload) {
  try {
    // Check if API key is configured
    if (!POSITIONSTACK_API_KEY) {
      console.warn('[GPS Provider: Positionstack] API key not configured, skipping...');
      return {
        provider: 'positionstack',
        success: false,
        apiWorked: false, // Can't work without API key
        error: 'API key not configured',
        coordinates: null,
      };
    }

    const normalizedRequest = normalizeRequestPayload(payload);

    console.log(
      '[GPS Provider: Positionstack] Searching coordinates for:',
      normalizedRequest.query
    );

    // Build URL with query params
    const url = new URL(API.PROVIDERS.GPS.POSITIONSTACK);
    url.searchParams.append('access_key', POSITIONSTACK_API_KEY);
    url.searchParams.append('query', normalizedRequest.query);
    url.searchParams.append('limit', '1');
    url.searchParams.append('country', 'BR');

    // Call Positionstack API directly
    const response = await fetchX({
      url: url.toString(),
      method: 'GET',
    });

    // Normalize response to universal POJO
    const normalized = normalizeResponseBody(response);

    console.log(
      '[GPS Provider: Positionstack] Response:',
      normalized.success ? 'Success' : 'Failed',
      normalized.success ? normalized.coordinates : normalized.error
    );

    return normalized;
  } catch (error) {
    console.error('[GPS Provider: Positionstack] API Error:', error.message);

    // API failed (network error, timeout, etc.)
    return {
      provider: 'positionstack',
      success: false,
      apiWorked: false, // API didn't work, try next provider
      error: error.message || 'Network error',
      coordinates: null,
    };
  }
}

/**
 * Normalizes incoming payload to Positionstack format
 * @param {Object} payload - Raw payload
 * @returns {Object} Positionstack-compatible payload
 */
function normalizeRequestPayload(payload) {
  const { address, number, city, region } = payload;

  // Build full address string for Positionstack
  const addressParts = [
    address && number ? `${address}, ${number}` : address || '',
    city || '',
    region || '',
    'Brasil',
  ].filter(Boolean);

  return {
    query: addressParts.join(', '),
  };
}

/**
 * Normalizes Positionstack response to universal POJO
 * @param {Object} response - Positionstack response
 * @returns {Object} Universal POJO
 *
 * Universal POJO structure for GPS:
 * {
 *   provider: string,
 *   success: boolean,
 *   apiWorked: boolean,
 *   coordinates: [lat, lng] | null,
 *   error: string | null,
 *   source: string
 * }
 */
function normalizeResponseBody(response) {
  const data = response?.data?.[0]; // Positionstack returns { data: [...] }

  // API error response
  if (response?.error) {
    return {
      provider: 'positionstack',
      success: false,
      apiWorked: true, // API worked but returned error
      coordinates: null,
      error: response.error.message || 'API error',
      source: 'positionstack',
    };
  }

  // No results found (address not found or insufficient data)
  if (!data || !data.latitude || !data.longitude) {
    return {
      provider: 'positionstack',
      success: false,
      apiWorked: true, // API worked, but address not found
      coordinates: null,
      error: 'Coordenadas não encontradas',
      source: 'positionstack',
    };
  }

  // Success - coordinates found
  return {
    provider: 'positionstack',
    success: true,
    apiWorked: true,
    coordinates: [parseFloat(data.latitude), parseFloat(data.longitude)],
    error: null,
    source: 'positionstack',
  };
}

// *******************************************************************************
// EXPORTS
// *******************************************************************************
export default {
  searchCoordinates,
  normalizeRequestPayload,
  normalizeResponseBody,
};
